import { useCallback, useEffect, useRef, useState } from 'react';
import { getGuildConfig, patchGuildConfig, resetGuildConfig } from '@/lib/api-client/guild-config';
import type { GuildConfigModule } from '@/lib/api-client/guild-config';
import { ApiError } from '@/lib/api-client/client';

/**
 * Shared hook for the 6 "custom" (field-level) modules — ai, logging,
 * analytics, prefix, tickets, welcome. Generalized from AiPage.tsx's
 * original hand-written version; AiPage itself is untouched (task: "AI is
 * already live... do not rewrite it"), this is only used by the newly
 * converted pages.
 *
 * Re-fetches on `guildId`/`module` change and ignores late responses from a
 * guild/module the user has already navigated away from — same
 * cross-guild-leakage guard as useToggleModule.
 */

type LoadState<TData> =
  | { kind: 'loading' }
  | { kind: 'forbidden' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'ready'; data: TData };

export function useGuildConfig<TData extends object>(
  guildId: string | undefined,
  module: GuildConfigModule,
) {
  const [state, setState] = useState<LoadState<TData>>({ kind: 'loading' });
  const [draft, setDraft] = useState<TData | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const requestToken = useRef(0);

  const load = useCallback(() => {
    if (!guildId) return;
    const token = ++requestToken.current;
    setState({ kind: 'loading' });
    setDraft(null);
    setSaveError(null);
    getGuildConfig<TData>(guildId, module)
      .then((res) => {
        if (requestToken.current !== token) return;
        setState({ kind: 'ready', data: res.data as TData });
      })
      .catch((err: unknown) => {
        if (requestToken.current !== token) return;
        if (err instanceof ApiError && err.code === 'FORBIDDEN') {
          setState({ kind: 'forbidden' });
        } else if (err instanceof ApiError && (err.code === 'BOT_UNREACHABLE' || err.code === 'BOT_DEGRADED')) {
          setState({ kind: 'unavailable', message: 'The bot is temporarily unreachable — try again shortly.' });
        } else {
          setState({ kind: 'unavailable', message: err instanceof ApiError ? err.message : 'Something went wrong loading this.' });
        }
      });
  }, [guildId, module]);

  useEffect(() => {
    load();
  }, [load]);

  const current = state.kind === 'ready' ? (draft ?? state.data) : null;
  const dirty = state.kind === 'ready' && draft !== null && JSON.stringify(draft) !== JSON.stringify(state.data);

  function update(changes: Partial<TData>) {
    if (!current) return;
    setDraft({ ...current, ...changes });
  }

  /** Diffs `draft` against the last-loaded `data` and PATCHes ONLY the
   * fields that actually changed — never the whole object. */
  async function save() {
    if (!guildId || state.kind !== 'ready' || !draft) return;
    const changed: Partial<TData> = {};
    for (const key of Object.keys(draft) as (keyof TData)[]) {
      if (JSON.stringify(draft[key]) !== JSON.stringify(state.data[key])) {
        changed[key] = draft[key];
      }
    }
    if (Object.keys(changed).length === 0) {
      setDraft(null);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await patchGuildConfig<TData>(guildId, module, changed);
      setState({ kind: 'ready', data: res.data as TData });
      setDraft(null);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    setDraft(null);
    setSaveError(null);
  }

  async function reset() {
    if (!guildId) return;
    setResetting(true);
    setSaveError(null);
    try {
      const res = await resetGuildConfig<TData>(guildId, module);
      setState({ kind: 'ready', data: res.data as TData });
      setDraft(null);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not reset — please try again.');
    } finally {
      setResetting(false);
    }
  }

  return { state, current, dirty, saving, saveError, resetting, update, save, discard, reset, reload: load };
}
